import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Search } from "lucide-react";
import { ArrowLineUp } from "@phosphor-icons/react/dist/ssr";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@repo/ui";
import { cn } from "@repo/utils";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

interface InventoryItem {
  id: string;
  ndc: string;
  itemName: string;
  drugType: string;
  label: string;
}

function formatSchedule(schedule: string): string {
  const scheduleMap: Record<string, string> = {
    schedule_ii: "Schedule II",
    schedule_iii: "Schedule III",
    schedule_iv: "Schedule IV",
    schedule_v: "Schedule V",
  };
  return (
    scheduleMap[schedule?.toLowerCase()] ||
    schedule?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
    ""
  );
}

export function QuickDispenseButton() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [items, setItems] = React.useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setQuery("");
      setItems([]);
    }
  }, [open]);

  React.useEffect(() => {
    const search = async () => {
      if (query.length < 1) {
        setItems([]);
        return;
      }
      setIsLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/inventory/search?q=${encodeURIComponent(query)}`,
          { credentials: "include" }
        );
        if (!res.ok) {
          throw new Error(
            `Inventory search failed (${res.status} ${res.statusText}) for query "${query}"`
          );
        }
        const data = await res.json();
        setItems(data.results || []);
      } catch (err) {
        setItems([]);
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (item: InventoryItem) => {
    setOpen(false);
    navigate({
      to: "/inventory",
      search: {
        action: "dispense",
        ndc: item.ndc,
        itemName: item.itemName,
      },
    });
  };

  const handleGoToInventory = () => {
    setOpen(false);
    navigate({ to: "/inventory" });
  };

  return (
    <>
      <Button
        size="sm"
        className="gap-1.5 h-9"
        data-tour="quick-dispense"
        onClick={() => setOpen(true)}
      >
        <ArrowLineUp weight="bold" size={15} />
        New Dispense
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px] p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-0">
            <DialogTitle className="text-base">
              Select Product to Dispense
            </DialogTitle>
          </DialogHeader>

          {/* Search input */}
          <div className="flex items-center gap-2.5 border-b border-border px-4 py-3 mt-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              type="text"
              placeholder="Search by product name or NDC..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {isLoading && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Results */}
          <div className="min-h-[100px] max-h-[320px] overflow-y-auto">
            {query.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Start typing to search your inventory
              </p>
            )}

            {!isLoading && query.length > 0 && items.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No products found for &ldquo;{query}&rdquo;
              </p>
            )}

            {items.length > 0 && (
              <div className="p-2">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={cn(
                      "group flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors",
                      "hover:bg-muted/60"
                    )}
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium">
                        {item.itemName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        NDC: {item.ndc}
                        {item.drugType
                          ? ` · ${formatSchedule(item.drugType)}`
                          : ""}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-4 py-3">
            <p className="text-xs text-muted-foreground">
              Can&apos;t find the product?{" "}
              <button
                type="button"
                onClick={handleGoToInventory}
                className="text-primary underline underline-offset-2 hover:opacity-80"
              >
                Go to Inventory
              </button>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
